import {ReactNode, useCallback, useState} from 'react';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {view, TextArea, assignTokens, tokens} from '@sail/ui';
import {DotComButton, useTheme} from '@stripe-internal/conversations-ui';
import {useClientAnalytics} from '../../../Providers/ClientProvider/useClientAnalytics';
import {DotComRatingButton} from './DotComRatingButton';
import type {RatingValue} from './types';
import {colors, transitions} from './theme';

const messages = defineMessages({
  title: {
    id: 'support_conversations_client.csat.dotcom.title',
    description: 'Title of CSAT survey form for dot com',
    defaultMessage: 'Help Stripe improve chat',
  },
  subheader: {
    id: 'support_conversations_client.csat.dotcom.subheader',
    description: 'Subheader text for the CSAT survey on dot com',
    defaultMessage: 'Your feedback is appreciated.',
  },
  ratingQuestion: {
    id: 'support_conversations_client.csat.dotcom.ratingQuestion',
    description: 'Rating question text for the CSAT survey on dot com',
    defaultMessage: 'How would you rate your chat experience?',
  },
  veryNegative: {
    id: 'support_conversations_client.csat.dotcom.veryNegative',
    description: 'Very negative label for the CSAT survey on dot com',
    defaultMessage: 'Very negative',
  },
  veryPositive: {
    id: 'support_conversations_client.csat.dotcom.veryPositive',
    description: 'Very positive label for the CSAT survey on dot com',
    defaultMessage: 'Very positive',
  },
  commentLabel: {
    id: 'support_conversations_client.csat.dotcom.commentLabel',
    description: 'Label for feedback text area in CSAT form for dot com',
    defaultMessage: "Is there anything you'd like to share?",
  },
  skipButton: {
    id: 'support_conversations_client.csat.dotcom.skipButton',
    description: 'Skip button text for the CSAT survey on dot com',
    defaultMessage: 'Skip',
  },
  submitButton: {
    id: 'support_conversations_client.csat.dotcom.submitButton',
    description: 'Submit button in CSAT form for dot com',
    defaultMessage: 'Submit feedback',
  },
});

type Props = {
  loading?: boolean;
  error?: ReactNode;
  analyticsParams?: Record<string, unknown>;
  onSubmit: ({rating, comment}: {rating: RatingValue; comment: string}) => void;
  onDismiss: () => void;
};

const allRatings: RatingValue[] = ['1', '2', '3', '4', '5'];
const COMMENT_MAX_LENGTH = 2000;

const HoverArrow = () => {
  return (
    <view.svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      aria-hidden="true"
      css={{
        position: 'relative',
        top: '1px',
        marginLeft: '5px',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: '2px',
      }}
    >
      <g fillRule="evenodd">
        <view.path
          d="M0 5h7"
          css={{
            opacity: 0,
            transition: transitions.default,
            '.DotComButton:hover:not([aria-disabled]) &': {
              opacity: 1,
            },
          }}
        />
        <view.path
          d="M1 1l4 4-4 4"
          css={{
            transform: 'none',
            transition: transitions.default,
            '.DotComButton:hover:not([aria-disabled]) &': {
              transform: 'translateX(3px)',
            },
          }}
        />
      </g>
    </view.svg>
  );
};

export const DotComSurveyForm = ({
  loading,
  error,
  onSubmit,
  onDismiss,
  analyticsParams = {},
}: Props) => {
  const intl = useIntl();
  const analytics = useClientAnalytics();
  const theme = useTheme();

  const [rating, setRating] = useState<RatingValue | null>(null);
  const [comment, setComment] = useState<string>('');

  const canSubmit = rating && !loading;

  const handleRatingSelected = useCallback(
    (selectedRating: RatingValue) => {
      analytics.action('support_conversations_dotcom_csat_survey_rating', {
        ...analyticsParams,
        rating: selectedRating,
      });
      setRating(selectedRating);
    },
    [analytics, analyticsParams],
  );

  const handleSubmit = useCallback(() => {
    if (rating) {
      onSubmit({rating, comment});
    }
  }, [rating, comment, onSubmit]);

  return (
    <view.div
      css={{
        backgroundColor: 'offset',
        borderRadius: 'medium',
        padding: '14px',
        boxShadow: 'top',
        stack: 'y',
        gap: 'medium',
        alignY: 'top',
        minHeight: '100%',
      }}
      uses={[
        assignTokens(tokens.color.text.action.primary, theme.linkColor),
        assignTokens(tokens.color.background.action.primary, theme.linkColor),
        assignTokens(tokens.font.heading.small.size, '15px'),
        assignTokens(tokens.font.heading.small.lineHeight, '1.4'),
        assignTokens(tokens.font.body.small.lineHeight, '1.4'),
        assignTokens(
          tokens.font.heading.small.weight,
          `${tokens.font.weight.semibold}`,
        ),
      ]}
    >
      <view.div css={{stack: 'y', gap: 'xxsmall'}}>
        <view.h1
          css={{
            font: 'heading.small',
            margin: 0,
            padding: 0,
            textColor: colors.neutral[800],
          }}
        >
          <FormattedMessage {...messages.title} />
        </view.h1>
        <view.p
          css={{
            font: 'heading.small.subdued',
            margin: 0,
            padding: 0,
            color: colors.neutral[700],
          }}
        >
          <FormattedMessage {...messages.subheader} />
        </view.p>
      </view.div>

      <view.div css={{stack: 'y', gap: 'xsmall', width: '100%'}}>
        <view.h2
          css={{
            font: 'heading.small',
            margin: 0,
            padding: 0,
            color: colors.neutral[700],
          }}
        >
          <FormattedMessage {...messages.ratingQuestion} />
        </view.h2>

        <view.div
          css={{
            display: 'flex',
            width: '100%',
            gap: '8px',
            justifyContent: 'space-between',
          }}
        >
          {allRatings.map((ratingOption) => (
            <DotComRatingButton
              accentColor={theme.linkColor}
              rating={ratingOption}
              dataTestId={`dotcom-survey-form-rating-${ratingOption}`}
              selected={ratingOption === rating}
              disabled={loading}
              key={ratingOption}
              onClick={() => handleRatingSelected(ratingOption)}
            />
          ))}
        </view.div>

        <view.div
          css={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            marginTop: '4px',
          }}
        >
          <view.span
            css={{
              fontSize: '13px',
              font: 'body.small',
              color: colors.neutral[500],
            }}
          >
            <FormattedMessage {...messages.veryNegative} />
          </view.span>
          <view.span
            css={{
              fontSize: '13px',
              font: 'body.small',
              color: colors.neutral[500],
            }}
          >
            <FormattedMessage {...messages.veryPositive} />
          </view.span>
        </view.div>
      </view.div>

      <TextArea
        css={{
          width: '100%',
        }}
        subviews={{
          input: {
            css: {
              borderRadius: 'medium',
            },
          },
        }}
        rows={5}
        resizeable={false}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        maxLength={COMMENT_MAX_LENGTH}
        data-testid="dotcom-survey-form-comment"
        readOnly={loading}
        label={intl.formatMessage(messages.commentLabel)}
      />

      <view.div css={{stack: 'y', gap: 'small', marginTop: 'auto'}}>
        {error && (
          <view.p
            css={{
              color: 'feedback.attention',
              font: 'body.small',
              margin: 0,
            }}
          >
            {error}
          </view.p>
        )}

        <view.div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <view.button
            onClick={onDismiss}
            disabled={loading}
            data-testid="dotcom-survey-form-skip-button"
            css={{
              transition: transitions.default,
              color: colors.neutral[500],
              border: 'none',
              fontWeight: 'semibold',
              background: 'none',
              ':hover:not([disabled])': {
                color: colors.neutral[800],
              },
            }}
          >
            <FormattedMessage {...messages.skipButton} />
          </view.button>

          <DotComButton
            data-testid="dotcom-survey-form-submit"
            type="primary"
            pending={loading}
            disabled={!canSubmit}
            accentColor={theme.linkColor}
            onPress={handleSubmit}
          >
            <FormattedMessage {...messages.submitButton} />
          </DotComButton>
        </view.div>
      </view.div>
    </view.div>
  );
};
